import { databases } from '@/lib/appwrite';
import { Query } from 'appwrite';

const DB_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const VISITS_COLLECTION_ID = import.meta.env.VITE_VISITS_COLLECTION_ID;
const ORGS_COLLECTION_ID = import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID;

// INTERFAZ PARA TIPAR LOS DOCUMENTOS
interface VisitDocument {
    $id: string;
    $collectionId: string;
    $databaseId: string;
    $createdAt: string;
    $updatedAt: string;
    $permissions: string[];
    userId: string;
    organizationId: string;
    status: string;
    visitDates: string[];
    visitPurpose: string;
    userComment?: string | null;
    branch?: string | null;
    usersId?: string[] | null;

}

interface OrganizationDocument {
    $id: string;
    $collectionId: string;
    $databaseId: string;
    $createdAt: string;
    $updatedAt: string;
    $permissions: string[];
    name: string;
    city?: string;
    address?: string;
}
interface GetVisitsParams {
    userId: string;
    page?: number;
    limit?: number;
    searchQuery?: string;
    statusFilter?: string;
    yearFilter?: string;    // nuevo
    monthFilter?: string;   // nuevo
}


export const getVisitsByUser = async ({
    userId,
    page = 1,
    limit = 12,
    searchQuery = '',
    statusFilter = 'all',
    yearFilter,          // NUEVO
    monthFilter          // NUEVO

}: GetVisitsParams) => {
    try {
        const hasSearch = searchQuery.trim().length > 0;

        // SIN BÚSQUEDA: Paginación del servidor (escalable)

        if (!hasSearch) {
            // Traer MÁS registros para poder filtrar por fecha en cliente
            const MAX_LIMIT_NO_SEARCH = 500;

            const queries = [
                Query.equal('userId', userId),
                Query.select(['$id', 'organizationId', 'status', 'visitDates', 'visitPurpose', 'branch', '$createdAt', 'usersId']),

                Query.limit(MAX_LIMIT_NO_SEARCH),
                Query.orderDesc('$createdAt')
            ];

            if (statusFilter !== 'all') {
                queries.push(Query.equal('status', statusFilter));
            }

            const visitsResponse = await databases.listDocuments<VisitDocument>(
                DB_ID,
                VISITS_COLLECTION_ID,
                queries
            );

            // Obtener nombres de organizaciones
            const orgIds = [...new Set(visitsResponse.documents.map(v => v.organizationId))];
            let organizationsMap = new Map<string, string>();

            if (orgIds.length > 0) {
                const orgsResponse = await databases.listDocuments<OrganizationDocument>(
                    DB_ID,
                    ORGS_COLLECTION_ID,
                    [
                        Query.equal('$id', orgIds),
                        Query.select(['$id', 'name']),
                        Query.limit(orgIds.length)
                    ]
                );

                orgsResponse.documents.forEach(org => {
                    organizationsMap.set(org.$id, org.name);
                });
            }

            const visitsWithOrgNames = visitsResponse.documents.map(visit => ({
                ...visit,
                organizationName: organizationsMap.get(visit.organizationId) || 'Sin nombre'
            }));

            //  FILTRAR POR FECHA (visitDates[1])
            let filteredByDate = visitsWithOrgNames;
            if (yearFilter && monthFilter) {
                filteredByDate = visitsWithOrgNames.filter(visit => {
                    const visitDate = new Date(visit.visitDates[1]);
                    const visitYear = visitDate.getUTCFullYear().toString();
                    const visitMonth = String(visitDate.getUTCMonth() + 1).padStart(2, '0');
                    return visitYear === yearFilter && visitMonth === monthFilter;
                });
            }

            //  PAGINACIÓN MANUAL después del filtro
            const total = filteredByDate.length;
            const offset = (page - 1) * limit;
            const paginatedVisits = filteredByDate.slice(offset, offset + limit);

            return {
                documents: paginatedVisits,
                total: total
            };
        }

        // CON BÚSQUEDA: Limita a 500 registros máximo
        const MAX_SEARCH_LIMIT = 500;

        const queries = [
            Query.equal('userId', userId),
            Query.select(['$id', 'organizationId', 'status', 'visitDates', 'visitPurpose', '$createdAt']),
            Query.limit(MAX_SEARCH_LIMIT),
            Query.orderDesc('$createdAt')
        ];

        if (statusFilter !== 'all') {
            queries.push(Query.equal('status', statusFilter));
        }

        const allVisitsResponse = await databases.listDocuments<VisitDocument>(
            DB_ID,
            VISITS_COLLECTION_ID,
            queries
        );

        // Obtener nombres de organizaciones
        const orgIds = [...new Set(allVisitsResponse.documents.map(v => v.organizationId))];
        let organizationsMap = new Map<string, string>();

        if (orgIds.length > 0) {
            const orgsResponse = await databases.listDocuments<OrganizationDocument>(
                DB_ID,
                ORGS_COLLECTION_ID,
                [
                    Query.equal('$id', orgIds),
                    Query.select(['$id', 'name']),
                    Query.limit(orgIds.length)
                ]
            );

            orgsResponse.documents.forEach(org => {
                organizationsMap.set(org.$id, org.name);
            });
        }

        const visitsWithOrgNames = allVisitsResponse.documents.map(visit => ({
            ...visit,
            organizationName: organizationsMap.get(visit.organizationId) || 'Sin nombre'
        }));

        // Filtrar por fecha de visita (visitDates[1])
        let filteredByDate = visitsWithOrgNames;
        if (yearFilter && monthFilter) {
            filteredByDate = visitsWithOrgNames.filter(visit => {
                const visitDate = new Date(visit.visitDates[1]);
                const visitYear = visitDate.getUTCFullYear().toString();
                const visitMonth = String(visitDate.getUTCMonth() + 1).padStart(2, '0');
                return visitYear === yearFilter && visitMonth === monthFilter;
            });
        }

        // Filtrado cliente
        const query = searchQuery.toLowerCase().trim();
        const filteredVisits = filteredByDate.filter(visit =>
            visit.organizationName?.toLowerCase().includes(query) ||
            visit.visitPurpose?.toLowerCase().includes(query) ||
            visit.branch?.toLowerCase().includes(query)
        );

        // Paginación manual
        const total = filteredVisits.length;
        const offset = (page - 1) * limit;
        const paginatedVisits = filteredVisits.slice(offset, offset + limit);

        return {
            documents: paginatedVisits,
            total: total,
            hasMoreRecords: allVisitsResponse.total > MAX_SEARCH_LIMIT
        };
    } catch (error: any) {
        console.error('Error fetching visits:', error);
        throw new Error(error.message || 'Error al cargar visitas');
    }
};


//Obtiene una visita específica por ID con detalles completos

export const getVisitById = async (visitId: string) => {
    try {
        const visit = await databases.getDocument<VisitDocument>(
            DB_ID,
            VISITS_COLLECTION_ID,
            visitId
        );

        // Obtener nombre de organización
        const org = await databases.getDocument<OrganizationDocument>(
            DB_ID,
            ORGS_COLLECTION_ID,
            visit.organizationId,
            // [Query.select(['$id', 'name', 'city', 'address'])]
        );

        return {
            ...visit,
            organizationName: org.name,
            organizationCity: org.city,
            organizationAddress: org.address
        };
    } catch (error: any) {
        console.error('Error fetching visit:', error);
        throw new Error(error.message || 'Error al cargar la visita');
    }
};


//Obtiene estadísticas de visitas del usuario

export const getVisitStats = async (userId: string) => {
    try {
        const [pending, completed, canceled, total] = await Promise.all([
            databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, [
                Query.equal('userId', userId),
                Query.equal('status', 'pending'),
                Query.limit(1)
            ]),
            databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, [
                Query.equal('userId', userId),
                Query.equal('status', 'completed'),
                Query.limit(1)
            ]),
            databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, [
                Query.equal('userId', userId),
                Query.equal('status', 'canceled'),
                Query.limit(1)
            ]),
            databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, [
                Query.equal('userId', userId),
                Query.limit(1)
            ])
        ]);

        return {
            pending: pending.total,
            completed: completed.total,
            canceled: canceled.total,
            total: total.total
        };
    } catch (error: any) {
        console.error('Error fetching stats:', error);
        return { pending: 0, completed: 0, canceled: 0, total: 0 };
    }
};

export const cancelVisit = async (visitId: string, userComment: string): Promise<void> => {
    try {
        await databases.updateDocument(
            DB_ID,
            VISITS_COLLECTION_ID,
            visitId,
            {
                status: 'canceled',
                userComment: userComment
            }
        );
    } catch (error: any) {
        console.error('Error canceling visit:', error);
        throw new Error(error.message || 'Error al cancelar la visita');
    }
};